import React from 'react';
import { CheckCircle2, SlidersHorizontal, AlertTriangle, XCircle } from 'lucide-react';

interface FilterPredicate {
  field?: string;
  operator?: string;
  value?: unknown;
  type?: string;
  entity_type?: string;
}

interface FilterGroup {
  query_type?: string;
  predicates?: FilterPredicate[];
}

interface SuggestedFilters {
  query_type?: string;
  predicates?: FilterGroup[];
}

interface AvailableField {
  field?: string;
  name?: string;
  label?: string;
  data_type?: string;
  is_metric?: boolean;
  valid_operators?: string[];
}

interface ValidationSummary {
  total_predicates?: number;
  stripped_predicates?: number;
}

interface StepProps {
  result: {
    ui_step: string;
    entity_type: string;
    suggested_filters?: SuggestedFilters;
    suggested_filter_summary?: string;
    available_fields?: AvailableField[];
    validation_summary?: ValidationSummary;
  };
  onSelect: (value: string, label: string) => void;
}

const OPERATOR_LABELS: Record<string, string> = {
  gt: 'greater than',
  gte: 'greater than or equal',
  lt: 'less than',
  lte: 'less than or equal',
  eq: 'equals',
  ne: 'not equals',
  contain: 'contains',
  does_not_contain: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  known: 'is known',
  unknown: 'is unknown',
  range: 'in range',
  in: 'is one of',
  relative: 'relative',
  before: 'before',
  after: 'after',
};

const NO_VALUE_OPERATORS = new Set(['known', 'unknown']);

const formatValue = (value: unknown) => {
  if (value === undefined || value === null || value === '') return '';
  if (typeof value === 'object') {
    if (value !== null && 'start' in (value as Record<string, unknown>) && 'end' in (value as Record<string, unknown>)) {
      const range = value as { start: unknown; end: unknown };
      return `${range.start} – ${range.end}`;
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/** Build a lookup from field name to display label */
const buildFieldLabelLookup = (fields?: AvailableField[]): Record<string, string> => {
  const lookup: Record<string, string> = {};
  if (!fields) return lookup;
  for (const f of fields) {
    const key = f.field || f.name || '';
    if (key && f.label) {
      lookup[key] = f.label;
    }
  }
  return lookup;
};

/** Check if a predicate has all required fields */
const isPredicateComplete = (predicate: FilterPredicate): boolean => {
  if (!predicate.field || !predicate.operator) return false;
  if (!predicate.entity_type) return false;
  if (!NO_VALUE_OPERATORS.has(predicate.operator) && (predicate.value === undefined || predicate.value === null || predicate.value === '')) {
    return false;
  }
  return true;
};

const formatPredicate = (predicate: FilterPredicate, fieldLabels: Record<string, string>) => {
  const fieldName = predicate.field || 'Unknown field';
  const displayName = fieldLabels[fieldName] || fieldName;
  const operator = OPERATOR_LABELS[predicate.operator || ''] || predicate.operator || 'matches';
  const value = formatValue(predicate.value);

  if (NO_VALUE_OPERATORS.has(predicate.operator || '')) {
    return `${displayName} ${operator}`;
  }

  const parts = [displayName, operator];
  if (value) parts.push(value);

  return parts.join(' ');
};

const FilterBuilderStep: React.FC<StepProps> = ({ result }) => {
  const groups = result.suggested_filters?.predicates || [];
  const hasPredicates = groups.some((group) => (group.predicates || []).length > 0);
  const fieldLabels = buildFieldLabelLookup(result.available_fields);
  const validation = result.validation_summary;

  return (
    <div className="w-full max-w-xl rounded-lg border border-border bg-card p-4">
      <div className="flex items-start gap-3">
        <div className="rounded-md border border-border bg-muted p-2">
          <SlidersHorizontal className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">Filters auto-generated</p>
          <p className="mt-1 text-xs text-muted-foreground">
            {result.suggested_filter_summary || 'ORA created initial filters from your request.'}
          </p>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Edit filters from the right sidebar
          </div>
          {validation && validation.stripped_predicates != null && validation.stripped_predicates > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700">
              <AlertTriangle className="h-3.5 w-3.5" />
              {validation.stripped_predicates} invalid filter{validation.stripped_predicates > 1 ? 's' : ''} removed during validation
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {hasPredicates ? groups.map((group, groupIndex) => {
          const predicates = group.predicates || [];
          if (!predicates.length) return null;

          return (
            <div key={`group-${groupIndex}`} className="rounded-md border border-border/80 bg-muted/30 p-3">
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Group {groupIndex + 1}
              </p>
              <div className="space-y-1.5">
                {predicates.map((predicate, predicateIndex) => {
                  const complete = isPredicateComplete(predicate);
                  return (
                    <div
                      key={`predicate-${groupIndex}-${predicateIndex}`}
                      className="flex items-start gap-1.5"
                    >
                      {complete ? (
                        <CheckCircle2 className="mt-0.5 h-3 w-3 flex-shrink-0 text-emerald-500" />
                      ) : (
                        <XCircle className="mt-0.5 h-3 w-3 flex-shrink-0 text-amber-500" />
                      )}
                      <p className={`text-xs ${complete ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {formatPredicate(predicate, fieldLabels)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }) : (
          <p className="text-xs text-muted-foreground">
            No filters were generated yet. Continue and ORA will keep refining the draft.
          </p>
        )}
      </div>
    </div>
  );
};

export default FilterBuilderStep;
